import { createLink } from '@tanstack/react-router';
import { EntityBadge, Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { Link, type LinkProps } from 'react-aria-components';

const styles = {
  card: cva({
    base: 'flex items-center w-full bg-primary border border-secondary rounded-xs shadow-xs',
    variants: {
      loading: {
        true: 'gap-3 px-4 py-5',
        false:
          'group gap-6 px-8 py-10 outline-none transition-colors hover:border-gray-utility-700 focus-visible:ring-1 focus-visible:ring-gray-utility-700',
      },
    },
  }),
  content: cva({ base: 'flex min-w-0 flex-1 items-center gap-6' }),
  meta: cva({ base: 'flex shrink-0 items-center gap-6' }),
  metaDot: cva({ base: 'w-2 h-2 shrink-0 rounded-full bg-gray-utility-400' }),
};

export interface EntityCardProps {
  id: string;
  name: string;
  href: string;
  meta?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
}

interface EntityCardLinkProps extends Omit<LinkProps, 'children'> {
  children: React.ReactNode;
}

const EntityCardLink = React.forwardRef<HTMLAnchorElement, EntityCardLinkProps>(({ children, ...other }, ref) => (
  <Link {...other} ref={ref}>
    {children}
  </Link>
));
EntityCardLink.displayName = 'EntityCardLink';

const RouterEntityCardLink = createLink(EntityCardLink);

function EntityCard(props: EntityCardProps) {
  const { id, name, href, meta, isLoading = false, className } = props;

  if (isLoading) {
    return (
      <div className={cx(styles.card({ loading: true }), className)} data-testid="entity-card-skeleton">
        <Skeleton variant="square" className="w-8 h-8 shrink-0 rounded" />
        <Skeleton variant="square" className="w-40 h-5" />
      </div>
    );
  }

  return (
    <RouterEntityCardLink
      to={href}
      preload="intent"
      className={cx(styles.card({ loading: false }), className)}
      data-testid="entity-card"
      data-entity-id={id}
    >
      <EntityBadge id={id} name={name} />

      <span className={styles.content()}>
        <Text size={TextSize.Md} weight={TextWeight.Medium} color={TextColor.Primary} className="truncate" title={name}>
          {name}
        </Text>
        {meta ? (
          <span className={styles.meta()}>
            <span aria-hidden="true" className={styles.metaDot()} />
            <Text size={TextSize.Sm} color={TextColor.Tertiary}>
              {meta}
            </Text>
          </span>
        ) : null}
      </span>
    </RouterEntityCardLink>
  );
}

export { EntityCard };
